import React, { useState, useCallback, useEffect } from 'react';
import { orderApi } from '../../lib/orderApi';
import { goldButton } from '../PageShell';

const cfgInput = { background: '#0A0A0A', border: '1px solid rgba(201,168,76,0.25)', color: '#F5F5F0', padding: '9px 11px', fontSize: '13px', outline: 'none', width: '100%' };
const statCard = { background: '#0A0A0A', border: '1px solid rgba(201,168,76,0.18)', padding: '14px', textAlign: 'center' };
const statNum = { fontFamily: "'Bebas Neue', sans-serif", fontSize: '28px', lineHeight: 1 };
const statLbl = { fontSize: '10px', letterSpacing: '1.5px', textTransform: 'uppercase', color: '#C2C1B6', marginTop: '4px' };

const EVENT_LABELS = {
  login_success: 'Login exitoso',
  login_failed: 'Login fallido',
  '2fa_success': '2FA exitoso',
  '2fa_failed': '2FA fallido',
  rate_limit_exceeded: 'Rate limit',
  sessions_invalidated: 'Sesiones invalidadas',
};

const EVENT_COLORS = {
  login_success: '#7FB77E',
  login_failed: '#E0736F',
  '2fa_success': '#7FB77E',
  '2fa_failed': '#E0736F',
  rate_limit_exceeded: '#C9A84C',
  sessions_invalidated: '#C9A84C',
};

const AdminAuditLogs = ({ isOpen }) => {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState({ event: '', success: '', email: '' });

  const loadLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (filter.event) params.event = filter.event;
      if (filter.success !== '') params.success = filter.success === 'true';
      if (filter.email) params.email = filter.email;
      
      const { data } = await orderApi.get('/auth/admin/audit-logs', { params });
      setLogs(data.logs || []);
      setStats(data.stats || null);
    } catch {
      setLogs([]);
      setStats(null);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    if (isOpen) loadLogs();
  }, [isOpen, loadLogs]);

  if (!isOpen) return null;

  return (
    <div className="mt-5 p-5" style={{ background: '#141414', border: '1px solid rgba(201,168,76,0.3)' }} data-testid="audit-panel">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div>
          <h3 className="font-display" style={{ fontSize: '18px', fontWeight: 700, color: '#F5F5F0' }}>Audit Logs de Seguridad</h3>
          <p className="font-body mt-1" style={{ fontSize: '12px', color: '#C2C1B6' }}>
            Registro de eventos de autenticación, rate limits y seguridad.
          </p>
        </div>
        <button onClick={loadLogs} style={goldButton} data-testid="audit-refresh">
          {loading ? 'Cargando...' : 'Actualizar'}
        </button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
          <div style={statCard}>
            <div style={{ ...statNum, color: '#7FB77E' }} data-testid="stat-logins">{stats.successful_logins_24h}</div>
            <div style={statLbl}>Logins exitosos (24h)</div>
          </div>
          <div style={statCard}>
            <div style={{ ...statNum, color: '#E0736F' }} data-testid="stat-failed">{stats.failed_logins_24h}</div>
            <div style={statLbl}>Logins fallidos (24h)</div>
          </div>
          <div style={statCard}>
            <div style={{ ...statNum, color: '#C9A84C' }} data-testid="stat-ratelimit">{stats.rate_limits_24h}</div>
            <div style={statLbl}>Rate limits (24h)</div>
          </div>
          <div style={statCard}>
            <div style={{ ...statNum, color: '#F5F5F0' }} data-testid="stat-total">{stats.total_events}</div>
            <div style={statLbl}>Total eventos</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-4 items-end">
        <div style={{ minWidth: '150px' }}>
          <label className="font-body" style={{ fontSize: '10px', color: '#C9A84C', textTransform: 'uppercase', letterSpacing: '1px' }}>
            Tipo de evento
          </label>
          <select
            value={filter.event}
            onChange={(e) => setFilter({ ...filter, event: e.target.value })}
            style={{ ...cfgInput, marginTop: '4px' }}
            data-testid="audit-filter-event"
          >
            <option value="">Todos</option>
            <option value="login_success">Login exitoso</option>
            <option value="login_failed">Login fallido</option>
            <option value="2fa_success">2FA exitoso</option>
            <option value="2fa_failed">2FA fallido</option>
            <option value="rate_limit_exceeded">Rate limit</option>
          </select>
        </div>
        <div style={{ minWidth: '120px' }}>
          <label className="font-body" style={{ fontSize: '10px', color: '#C9A84C', textTransform: 'uppercase', letterSpacing: '1px' }}>
            Resultado
          </label>
          <select
            value={filter.success}
            onChange={(e) => setFilter({ ...filter, success: e.target.value })}
            style={{ ...cfgInput, marginTop: '4px' }}
            data-testid="audit-filter-success"
          >
            <option value="">Todos</option>
            <option value="true">Exitoso</option>
            <option value="false">Fallido</option>
          </select>
        </div>
        <div style={{ minWidth: '180px' }}>
          <label className="font-body" style={{ fontSize: '10px', color: '#C9A84C', textTransform: 'uppercase', letterSpacing: '1px' }}>
            Email
          </label>
          <input
            type="text"
            placeholder="Buscar email..."
            value={filter.email}
            onChange={(e) => setFilter({ ...filter, email: e.target.value })}
            style={{ ...cfgInput, marginTop: '4px' }}
            data-testid="audit-filter-email"
          />
        </div>
        <button onClick={loadLogs} style={{ ...goldButton, marginBottom: '1px' }} data-testid="audit-apply-filter">
          Filtrar
        </button>
      </div>

      {/* Logs Table */}
      {logs.length === 0 ? (
        <p className="font-body" style={{ fontSize: '13px', color: '#C2C1B6', textAlign: 'center', padding: '20px' }}>
          {loading ? 'Cargando logs...' : 'No hay eventos registrados.'}
        </p>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid rgba(201,168,76,0.25)' }}>
                <th style={{ textAlign: 'left', padding: '10px 8px', color: '#C9A84C', fontWeight: 600, fontSize: '10px', letterSpacing: '1px', textTransform: 'uppercase' }}>
                  Fecha
                </th>
                <th style={{ textAlign: 'left', padding: '10px 8px', color: '#C9A84C', fontWeight: 600, fontSize: '10px', letterSpacing: '1px', textTransform: 'uppercase' }}>
                  Evento
                </th>
                <th style={{ textAlign: 'left', padding: '10px 8px', color: '#C9A84C', fontWeight: 600, fontSize: '10px', letterSpacing: '1px', textTransform: 'uppercase' }}>
                  Resultado
                </th>
                <th style={{ textAlign: 'left', padding: '10px 8px', color: '#C9A84C', fontWeight: 600, fontSize: '10px', letterSpacing: '1px', textTransform: 'uppercase' }}>
                  Email
                </th>
                <th style={{ textAlign: 'left', padding: '10px 8px', color: '#C9A84C', fontWeight: 600, fontSize: '10px', letterSpacing: '1px', textTransform: 'uppercase' }}>
                  IP
                </th>
                <th style={{ textAlign: 'left', padding: '10px 8px', color: '#C9A84C', fontWeight: 600, fontSize: '10px', letterSpacing: '1px', textTransform: 'uppercase' }}>
                  Detalles
                </th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log, idx) => (
                <tr key={`${log.timestamp}-${idx}`} style={{ borderBottom: '1px solid rgba(201,168,76,0.1)' }} data-testid={`audit-row-${idx}`}>
                  <td style={{ padding: '10px 8px', color: '#C2C1B6', whiteSpace: 'nowrap' }}>
                    {log.timestamp ? new Date(log.timestamp).toLocaleString('es-PR', { dateStyle: 'short', timeStyle: 'medium' }) : '-'}
                  </td>
                  <td style={{ padding: '10px 8px' }}>
                    <span
                      style={{
                        display: 'inline-block',
                        padding: '3px 8px',
                        fontSize: '10px',
                        fontWeight: 600,
                        background: `${EVENT_COLORS[log.event] || '#C9A84C'}22`,
                        color: EVENT_COLORS[log.event] || '#C9A84C',
                        borderRadius: '3px',
                      }}
                    >
                      {EVENT_LABELS[log.event] || log.event}
                    </span>
                  </td>
                  <td style={{ padding: '10px 8px' }}>
                    {log.success ? (
                      <span style={{ color: '#7FB77E' }}>✓ OK</span>
                    ) : (
                      <span style={{ color: '#E0736F' }}>✗ Fallo</span>
                    )}
                  </td>
                  <td style={{ padding: '10px 8px', color: '#F5F5F0' }}>{log.email || '-'}</td>
                  <td style={{ padding: '10px 8px', color: '#C2C1B6', fontFamily: 'monospace', fontSize: '11px' }}>
                    {log.ip || '-'}
                  </td>
                  <td style={{ padding: '10px 8px', color: '#9A7522', fontSize: '11px', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={JSON.stringify(log.details)}>
                    {log.details ? Object.entries(log.details).map(([k, v]) => `${k}: ${v}`).join(', ') : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="font-body mt-3" style={{ fontSize: '11px', color: '#888880' }}>
        Mostrando {logs.length} eventos. Los logs de auditoría ayudan a detectar actividad sospechosa y cumplir requisitos de seguridad.
      </p>
    </div>
  );
};

export default AdminAuditLogs;
